/* global Office */
const DIALOG_OPEN_BURST_GUARD_MS = 600;
const DIALOG_OPEN_BURST_GUARD_KEY = "__carousel_dialog_open_burst_guard__";
function completeEvent(event) {
    try {
        event?.completed();
    }
    catch {
        // Best effort only.
    }
}
function mergeDialogOptions(defaults, overrides) {
    return {
        height: overrides?.height ?? defaults?.height ?? 60,
        width: overrides?.width ?? defaults?.width ?? 40,
        displayInIframe: overrides?.displayInIframe ?? defaults?.displayInIframe ?? false,
        promptBeforeOpen: overrides?.promptBeforeOpen ?? defaults?.promptBeforeOpen ?? false,
    };
}
export function createDialogOpener(options = {}) {
    let currentOpenDialog = null;
    let currentDialogRef = null;
    const notifyState = () => {
        options.onStateChange?.({
            currentOpenDialog,
            currentDialogRef,
        });
    };
    const getState = () => ({
        currentOpenDialog,
        currentDialogRef,
    });
    const resetDialogState = () => {
        currentOpenDialog = null;
        currentDialogRef = null;
        notifyState();
    };
    const getCurrentOpenDialog = () => currentOpenDialog;
    const getCurrentDialogRef = () => currentDialogRef;
    const isDialogOpen = () => currentOpenDialog !== null;
    const setDialogState = (dialogType, dialogRef = null) => {
        currentOpenDialog = dialogType;
        currentDialogRef = dialogRef;
        notifyState();
    };
    const closeCurrentDialog = () => {
        try {
            currentDialogRef?.close();
        }
        catch {
            // Ignore close failures.
        }
        finally {
            resetDialogState();
        }
    };
    const openDialog = (openOptions) => {
        if (!openOptions.allowWhenBusy) {
            const globalScope = globalThis;
            const now = Date.now();
            const lastOpen = globalScope[DIALOG_OPEN_BURST_GUARD_KEY];
            const isDuplicateBurst = lastOpen?.dialogType === String(openOptions.dialogType) &&
                typeof lastOpen?.requestedAt === "number" &&
                now - lastOpen.requestedAt < DIALOG_OPEN_BURST_GUARD_MS;
            globalScope[DIALOG_OPEN_BURST_GUARD_KEY] = {
                dialogType: String(openOptions.dialogType),
                requestedAt: now,
            };
            if (isDuplicateBurst) {
                completeEvent(openOptions.event);
                return;
            }
        }
        if (!openOptions.allowWhenBusy && currentOpenDialog !== null) {
            completeEvent(openOptions.event);
            return;
        }
        currentOpenDialog = openOptions.dialogType;
        currentDialogRef = null;
        notifyState();
        const dialogOptions = mergeDialogOptions(options.defaultDialogOptions, openOptions.options);
        let done = false;
        let dialogRef = null;
        let commandEventCompleted = false;
        const completeCommandEvent = () => {
            if (commandEventCompleted) {
                return;
            }
            commandEventCompleted = true;
            completeEvent(openOptions.event);
        };
        const finish = () => {
            if (done) {
                return;
            }
            done = true;
            resetDialogState();
            completeCommandEvent();
        };
        const close = () => {
            try {
                dialogRef?.close();
            }
            catch {
                // Best effort only.
            }
            finish();
        };
        Office.context.ui.displayDialogAsync(openOptions.dialogUrl, dialogOptions, (result) => {
            if (result.status === Office.AsyncResultStatus.Failed) {
                void Promise.resolve(openOptions.onOpenFailed?.(result.error)).catch((error) => {
                    if (options.loggerPrefix) {
                        console.warn(`${options.loggerPrefix} onOpenFailed handler failed`, error);
                    }
                });
                if (options.loggerPrefix) {
                    console.warn(`${options.loggerPrefix} Failed to open dialog`, {
                        dialogType: openOptions.dialogType,
                        error: result.error,
                    });
                }
                finish();
                return;
            }
            dialogRef = result.value;
            currentDialogRef = dialogRef;
            notifyState();
            void Promise.resolve(openOptions.onOpened?.(dialogRef)).catch((error) => {
                if (options.loggerPrefix) {
                    console.warn(`${options.loggerPrefix} onOpened handler failed`, error);
                }
            });
            // Office command events must be completed promptly after launching the UI.
            // Waiting until dialog close can trigger "UI less Add-in command timed out".
            completeCommandEvent();
            dialogRef.addEventHandler(Office.EventType.DialogMessageReceived, (args) => {
                if (!openOptions.onMessage) {
                    return;
                }
                const context = {
                    dialogType: openOptions.dialogType,
                    dialog: dialogRef,
                    args,
                    message: args.message,
                    event: openOptions.event,
                    finish,
                    close,
                    getState,
                };
                void Promise.resolve(openOptions.onMessage(context)).catch((error) => {
                    if (options.loggerPrefix) {
                        console.warn(`${options.loggerPrefix} onMessage handler failed`, error);
                    }
                });
            });
            dialogRef.addEventHandler(Office.EventType.DialogEventReceived, (args) => {
                if (!openOptions.onDialogEvent) {
                    finish();
                    return;
                }
                const context = {
                    dialogType: openOptions.dialogType,
                    dialog: dialogRef,
                    args,
                    event: openOptions.event,
                    finish,
                    close,
                    getState,
                };
                void Promise.resolve(openOptions.onDialogEvent(context)).catch((error) => {
                    if (options.loggerPrefix) {
                        console.warn(`${options.loggerPrefix} onDialogEvent handler failed`, error);
                    }
                });
            });
        });
    };
    const openGuardedDialog = (openOptions) => {
        if (openOptions.requiresAuth && options.isAuthenticated && !options.isAuthenticated()) {
            if (options.onRequireAuthentication) {
                options.onRequireAuthentication({
                    requestedDialogType: openOptions.dialogType,
                    event: openOptions.event,
                });
            }
            else {
                completeEvent(openOptions.event);
            }
            return;
        }
        openDialog(openOptions);
    };
    return {
        openDialog,
        openGuardedDialog,
        setDialogState,
        getCurrentOpenDialog,
        getCurrentDialogRef,
        closeCurrentDialog,
        resetDialogState,
        isDialogOpen,
    };
}
